/**
 * StoreRevenuesPage — iter-299i (Feb 2026)
 *
 * E-commerce order revenue hub. READ-ONLY — the merchant platform owns
 * the lifecycle fields (`status`, payment flow) and the customer platform
 * owns `receipt_number`; this screen never writes them back.
 *
 * Route: /dashboard/finance/cloudgm/merchants/store-revenues
 *
 * Layout:
 *   • Five KPI cards (customer-paid, commissions earned, admin fees,
 *     pending-with-merchants, settled-to-wallets)
 *   • Filter bar (date range, order status, wallet status, store,
 *     payment method, search by order_id / receipt_number)
 *   • Table of orders with per-row totals and status badges
 *
 * Refresh model: 30-second polling. No manual button — the user asked
 * for "something not manual".
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ShoppingBag, TrendingUp, Wallet, Clock, CheckCircle2, Receipt,
  Search, Filter, Loader2, Store, Hash, Package, CreditCard,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { getAuthHeaders } from '../../utils/authHeaders';
import DirhamPrice from '../../components/DirhamPrice';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 30_000;

const STATUS_COPY = {
  pending:    { ar: 'قيد الانتظار',  en: 'Pending',    cls: 'bg-amber-500/15 text-amber-200 border-amber-500/40' },
  confirmed:  { ar: 'مؤكّد',          en: 'Confirmed',  cls: 'bg-sky-500/15 text-sky-200 border-sky-500/40' },
  processing: { ar: 'قيد التجهيز',    en: 'Processing', cls: 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40' },
  shipped:    { ar: 'تم الشحن',       en: 'Shipped',    cls: 'bg-blue-500/15 text-blue-200 border-blue-500/40' },
  delivered:  { ar: 'تم التسليم',     en: 'Delivered',  cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40' },
  cancelled:  { ar: 'ملغي',           en: 'Cancelled',  cls: 'bg-rose-500/15 text-rose-200 border-rose-500/40' },
};

const WALLET_COPY = {
  settled:   { ar: 'في محفظة التاجر', en: 'In merchant wallet', cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40' },
  pending:   { ar: 'معلّق',           en: 'Pending',            cls: 'bg-amber-500/15 text-amber-200 border-amber-500/40' },
  cancelled: { ar: 'ملغي',            en: 'Cancelled',          cls: 'bg-rose-500/15 text-rose-200 border-rose-500/40' },
};

export default function StoreRevenuesPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((a, e) => (isAr ? a : e), [isAr]);

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stores, setStores] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    status: '',
    wallet_status: '',
    store_id: '',
    payment_method: '',
    q: '',
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchRows = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const params = {};
      Object.entries(filtersRef.current).forEach(([k, v]) => {
        if (v && String(v).trim() !== '') params[k] = v;
      });
      const r = await axios.get(`${API}/admin/store-revenues`, {
        headers: getAuthHeaders(),
        params,
      });
      setRows(r.data?.data?.rows || []);
      setKpis(r.data?.data?.kpis || null);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Load stores for the dropdown ONCE on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/store-revenues/stores`, { headers: getAuthHeaders() });
        setStores(r.data?.data || []);
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  // Initial fetch + poll.
  useEffect(() => {
    fetchRows(false);
    const id = setInterval(() => fetchRows(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchRows]);

  // Refetch when filters change (debounced via inline setTimeout).
  useEffect(() => {
    const h = setTimeout(() => fetchRows(false), 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_from, filters.date_to, filters.status, filters.wallet_status, filters.store_id, filters.payment_method, filters.q]);

  const paymentMethods = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.payment_method && r.payment_method !== '—') s.add(r.payment_method); });
    return [...s];
  }, [rows]);

  return (
    <DashboardLayout
      title="إيرادات المتاجر"
      titleEn="Store Revenues"
      subtitle="سجل شامل بإيرادات كل متجر إلكتروني وحالة محفظة التاجر"
      subtitleEn="Comprehensive ledger of every e-commerce store's revenue and wallet routing"
      screenCode="FIN-MERCH-STORE-REV"
      backPath="/dashboard/finance/cloudgm/merchants/revenues"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                     path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',              path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'الإيرادات',              labelEn: 'Revenues',                    path: '/dashboard/finance/cloudgm/merchants/revenues' },
        { labelAr: 'إيرادات المتاجر',         labelEn: 'Store Revenues',              path: null },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="store-revenues-root">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi icon={ShoppingBag} color="#38bdf8"
               label={t('إجمالي ما دفعه العملاء', 'Customer Paid')}
               value={kpis?.total_customer_paid}
               hint={`${kpis?.orders_count || 0} ${t('طلب', 'orders')}`}
               loading={loading} testid="kpi-customer-paid" />
          <Kpi icon={TrendingUp} color="#10b981"
               label={t('إجمالي العمولات', 'Commissions Earned')}
               value={kpis?.total_commissions}
               hint={t('من الطلبات المسلّمة فقط', 'From delivered only')}
               loading={loading} testid="kpi-commissions" />
          <Kpi icon={Receipt} color="#eab308"
               label={t('إجمالي الرسوم الإدارية', 'Admin Fees')}
               value={kpis?.total_admin_fees}
               hint={t('من الطلبات المسلّمة فقط', 'From delivered only')}
               loading={loading} testid="kpi-admin-fees" />
          <Kpi icon={Clock} color="#f59e0b"
               label={t('معلّق لدى التجّار', 'Pending at Merchants')}
               value={kpis?.pending_with_merchants}
               hint={`${kpis?.pending_count || 0} ${t('طلب', 'orders')}`}
               loading={loading} testid="kpi-pending" />
          <Kpi icon={Wallet} color="#a855f7"
               label={t('مُحوَّل إلى المحافظ', 'Settled to Wallets')}
               value={kpis?.settled_to_wallets}
               hint={`${kpis?.settled_count || 0} ${t('طلب', 'orders')}`}
               loading={loading} testid="kpi-settled" />
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-5"
          data-testid="store-revenues-filters"
        >
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-sky-300" />
            <span className="text-white/80 text-sm font-semibold">{t('المرشّحات', 'Filters')}</span>
            {refreshing && <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-300 ml-auto" />}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterInput icon={Search} testid="store-rev-filter-q"
                         placeholder={t('بحث برقم الطلب أو الإيصال', 'Search order / receipt')}
                         value={filters.q}
                         onChange={(v) => setFilters((f) => ({ ...f, q: v }))} />
            <FilterSelect testid="store-rev-filter-status"
                          label={t('حالة الطلب', 'Order status')}
                          value={filters.status}
                          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <option value="">{t('كل الحالات', 'All')}</option>
              {Object.keys(STATUS_COPY).map((k) => (
                <option key={k} value={k}>{isAr ? STATUS_COPY[k].ar : STATUS_COPY[k].en}</option>
              ))}
            </FilterSelect>
            <FilterSelect testid="store-rev-filter-wallet"
                          label={t('حالة المحفظة', 'Wallet state')}
                          value={filters.wallet_status}
                          onChange={(v) => setFilters((f) => ({ ...f, wallet_status: v }))}>
              <option value="">{t('كل الحالات', 'All')}</option>
              {Object.keys(WALLET_COPY).map((k) => (
                <option key={k} value={k}>{isAr ? WALLET_COPY[k].ar : WALLET_COPY[k].en}</option>
              ))}
            </FilterSelect>
            <FilterSelect testid="store-rev-filter-store"
                          label={t('المتجر', 'Store')}
                          value={filters.store_id}
                          onChange={(v) => setFilters((f) => ({ ...f, store_id: v }))}>
              <option value="">{t('كل المتاجر', 'All stores')}</option>
              {stores.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
            </FilterSelect>
            <FilterSelect testid="store-rev-filter-payment"
                          label={t('طريقة الدفع', 'Payment method')}
                          value={filters.payment_method}
                          onChange={(v) => setFilters((f) => ({ ...f, payment_method: v }))}>
              <option value="">{t('كل الطرق', 'All')}</option>
              {paymentMethods.map((p) => (<option key={p} value={p}>{p}</option>))}
            </FilterSelect>
            <FilterInput icon={null} testid="store-rev-filter-date-from"
                         type="date" placeholder={t('من تاريخ', 'From')}
                         value={filters.date_from}
                         onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))} />
            <FilterInput icon={null} testid="store-rev-filter-date-to"
                         type="date" placeholder={t('إلى تاريخ', 'To')}
                         value={filters.date_to}
                         onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} />
          </div>
        </motion.div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
             data-testid="store-revenues-table-wrap">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03] text-white/70 text-xs">
                  <Th icon={Hash}>{t('رقم الطلب', 'Order #')}</Th>
                  <Th icon={Receipt}>{t('رقم الإيصال', 'Receipt #')}</Th>
                  <Th icon={Store}>{t('المتجر', 'Store')}</Th>
                  <Th>{t('السعر الأساسي', 'Base')}</Th>
                  <Th>{t('العمولة %', 'Comm %')}</Th>
                  <Th>{t('مبلغ العمولة', 'Commission')}</Th>
                  <Th>{t('الرسوم الإدارية', 'Admin Fee')}</Th>
                  <Th>{t('إجمالي الطلب', 'Total')}</Th>
                  <Th>{t('حالة الطلب', 'Status')}</Th>
                  <Th>{t('حالة المحفظة', 'Wallet')}</Th>
                  <Th icon={CreditCard}>{t('الدفع', 'Payment')}</Th>
                  <Th>{t('التاريخ', 'Date')}</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="py-20 text-center text-white/40">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t('جارٍ التحميل…', 'Loading…')}
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={12} className="py-20 text-center text-white/40">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <div>{t('لا توجد طلبات تطابق المرشّحات الحالية', 'No orders match the current filters')}</div>
                  </td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.order_id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid="store-rev-row">
                    <Td mono><span dir="ltr">{r.order_id}</span></Td>
                    <Td mono>{r.receipt_number
                      ? <span className="text-emerald-300" dir="ltr">{r.receipt_number}</span>
                      : <span className="text-white/30">—</span>}
                    </Td>
                    <Td>{r.store_name || '—'}</Td>
                    <Td mono><DirhamPrice value={r.base_total} /></Td>
                    <Td mono>{r.commission_pct == null ? <span className="text-white/30">—</span> : `${r.commission_pct}%`}</Td>
                    <Td mono><DirhamPrice value={r.commission_total} /></Td>
                    <Td mono><DirhamPrice value={r.admin_fee_total} /></Td>
                    <Td mono className="text-white font-semibold"><DirhamPrice value={r.final_total} /></Td>
                    <Td><StatusPill map={STATUS_COPY} key_={r.status} isAr={isAr} /></Td>
                    <Td><StatusPill map={WALLET_COPY} key_={r.wallet_status} isAr={isAr} /></Td>
                    <Td className="text-white/80">{r.payment_method}</Td>
                    <Td className="text-white/60" mono>{formatDate(r.created_at, isAr)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between text-xs text-white/50">
            <span data-testid="store-rev-count">{rows.length} {t('طلب', 'orders')}</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t('يُحدَّث تلقائياً كل 30 ثانية', 'Live — refreshes every 30s')}
            </span>
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}

/* ───────────────────────────── sub-components ───────────────────────────── */

function Kpi({ icon: Icon, color, label, value, hint, loading, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5"
         data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
             style={{ backgroundColor: `${color}22`, border: `1px solid ${color}44` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-[10px] text-white/50 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-white text-lg font-bold">
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : (value == null ? <span className="text-white/30">—</span> : <DirhamPrice value={value} />)}
      </div>
      {hint && <div className="text-[10.5px] text-white/40 mt-0.5">{hint}</div>}
    </div>
  );
}

function FilterInput({ icon: Icon, placeholder, value, onChange, type = 'text', testid }) {
  return (
    <div className="relative">
      {Icon && <Icon className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30"
                     style={{ insetInlineStart: '0.75rem' }} />}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full h-10 rounded-xl bg-black/40 border border-white/10 text-white text-sm text-white placeholder-white/30 outline-none focus:border-sky-500/60 transition-colors ${Icon ? 'ps-9 pe-3' : 'px-3'} [color-scheme:dark]`}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, children, testid }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        aria-label={label}
        className="w-full h-10 rounded-xl bg-black/40 border border-white/10 text-white text-sm px-3 outline-none focus:border-sky-500/60 transition-colors [color-scheme:dark]"
      >
        {children}
      </select>
    </div>
  );
}

function Th({ children, icon: Icon }) {
  return (
    <th className="px-3 py-2.5 text-start whitespace-nowrap font-semibold">
      <span className="inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 opacity-60" />}
        {children}
      </span>
    </th>
  );
}

function Td({ children, mono, className = '' }) {
  const monoCls = mono ? 'font-mono' : '';
  return <td className={`px-3 py-2.5 whitespace-nowrap text-white/85 ${monoCls} ${className}`}>{children}</td>;
}

function StatusPill({ map, key_, isAr }) {
  const def = map[key_] || { ar: key_, en: key_, cls: 'bg-white/5 text-white/60 border-white/10' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${def.cls}`}>
      {isAr ? def.ar : def.en}
    </span>
  );
}

function formatDate(iso, isAr) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const time = d.toLocaleTimeString(isAr ? 'ar-EG' : 'en-US', {
      hour: '2-digit', minute: '2-digit',
    });
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}
